"use client";

import { useEffect, useId, useRef, useState } from "react";
import type { CountryCode } from "libphonenumber-js/min";
import { type CountryOption } from "@/lib/countries";
import { CountryDialPicker } from "@/components/checkout/country-combobox";
import { formatAsYouType } from "@/lib/phone";
import { formatPostcode, lookupNlAddress, postcodeExample, splitHouseNumber } from "@/lib/address-validation";

const baseInput =
  "w-full h-12 px-4 bg-background border text-[16px] text-foreground placeholder:text-text-tertiary outline-none focus:border-foreground transition-colors rounded-xl";

export function FormField({
  label,
  name,
  type = "text",
  value,
  onChange,
  onBlur,
  required = false,
  placeholder,
  autoComplete,
  hint,
  error,
  inputMode,
  disabled,
}: {
  label: string;
  name: string;
  type?: string;
  value: string;
  onChange: (val: string) => void;
  onBlur?: () => void;
  required?: boolean;
  placeholder?: string;
  autoComplete?: string;
  hint?: string;
  error?: string;
  inputMode?: "text" | "numeric" | "tel" | "email";
  disabled?: boolean;
}) {
  return (
    <div>
      <label htmlFor={name} className="block text-[14px] font-medium text-text-secondary mb-1.5">
        {label} {required && <span className="text-negative">*</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        inputMode={inputMode}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        autoComplete={autoComplete}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${name}-error` : hint ? `${name}-hint` : undefined}
        className={`${baseInput} ${error ? "border-negative" : "border-border"} ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
        style={{ transitionDuration: "var(--duration-fast)" }}
      />
      {error && (
        <p id={`${name}-error`} className="text-[13px] text-negative mt-1">
          {error}
        </p>
      )}
      {hint && !error && (
        <p id={`${name}-hint`} className="text-[12px] text-text-tertiary mt-1">
          {hint}
        </p>
      )}
    </div>
  );
}

export function PhoneField({
  label,
  hint,
  number,
  country,
  onNumberChange,
  onCountryChange,
  onBlur,
  options,
  required = false,
  error,
  searchPlaceholder,
  noResultsLabel,
}: {
  label: string;
  hint?: string;
  number: string;
  country: CountryCode;
  onNumberChange: (val: string) => void;
  onCountryChange: (code: CountryCode) => void;
  onBlur?: () => void;
  options: readonly CountryOption[];
  required?: boolean;
  error?: string;
  searchPlaceholder?: string;
  noResultsLabel?: string;
}) {
  const id = useId();
  const dialCode = options.find((o) => o.code === country)?.dialCode ?? "";
  return (
    <div>
      <label htmlFor={id} className="block text-[14px] font-medium text-text-secondary mb-1.5">
        {label} {required && <span className="text-negative">*</span>}
      </label>
      <div
        className={`flex h-12 bg-background border rounded-xl overflow-visible focus-within:border-foreground transition-colors ${
          error ? "border-negative" : "border-border"
        }`}
        style={{ transitionDuration: "var(--duration-fast)" }}
      >
        <CountryDialPicker
          value={country}
          onChange={onCountryChange}
          options={options}
          searchPlaceholder={searchPlaceholder}
          noResultsLabel={noResultsLabel}
        />
        <span className="flex items-center px-3 text-[16px] text-text-secondary select-none">
          {dialCode}
        </span>
        <input
          id={id}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          value={number}
          onChange={(e) => onNumberChange(formatAsYouType(e.target.value, country))}
          onBlur={onBlur}
          placeholder="6 12345678"
          aria-invalid={error ? true : undefined}
          className="flex-1 h-full pl-2 pr-4 bg-transparent text-[16px] text-foreground placeholder:text-text-tertiary outline-none"
        />
      </div>
      {error && <p className="text-[13px] text-negative mt-1">{error}</p>}
      {hint && !error && <p className="text-[12px] text-text-tertiary mt-1">{hint}</p>}
    </div>
  );
}

// AddressFields — country-aware postcode + houseNr + street + city. For
// NL it auto-fills street + city from PDOK after both postcode and
// houseNumber are populated. Lookup is debounced + cancellable so fast
// typers don't fire stale requests.
export function AddressFields({
  country,
  postalCode,
  setPostalCode,
  houseNumber,
  setHouseNumber,
  addition,
  setAddition,
  street,
  setStreet,
  city,
  setCity,
  errors,
  onBlur,
  labels,
  hints,
}: {
  country: CountryCode;
  postalCode: string;
  setPostalCode: (v: string) => void;
  houseNumber: string;
  setHouseNumber: (v: string) => void;
  addition: string;
  setAddition: (v: string) => void;
  street: string;
  setStreet: (v: string) => void;
  city: string;
  setCity: (v: string) => void;
  errors: { postalCode?: string; houseNumber?: string; street?: string; city?: string };
  onBlur: (field: "postalCode" | "houseNumber" | "street" | "city") => void;
  labels: { postalCode: string; houseNumber: string; addition: string; street: string; city: string; additionPlaceholder?: string };
  hints?: { lookup?: string; lookupNotFound?: string };
}) {
  const [lookupState, setLookupState] = useState<"idle" | "loading" | "notFound">("idle");
  const abortRef = useRef<AbortController | null>(null);

  // Auto-format the postcode shape per country as the user types so the
  // value we validate matches what they see (e.g. NL "1234ab" → "1234 AB").
  const onPostcodeChange = (raw: string) => setPostalCode(formatPostcode(raw, country));

  // Only NL needs the numeric house number + separate toevoeging split
  // (PostNL/MyParcel number + number_suffix). For other countries house
  // numbers are free-form (e.g. Irish/Spanish alphanumerics), so we keep
  // the field as-is and never divert or strip; gating here avoids
  // corrupting non-NL addresses. For NL: if a letter/suffix is typed or
  // pasted into Huisnr, divert it to the toevoeging field (when empty)
  // rather than dropping it.
  const splitsHouseNumber = country === "NL";
  const onHouseNumberChange = (raw: string) => {
    if (!splitsHouseNumber) {
      setHouseNumber(raw);
      return;
    }
    const { number, addition: diverted } = splitHouseNumber(raw);
    setHouseNumber(number);
    if (diverted && !addition.trim()) setAddition(diverted);
  };
  // Keep Unicode letters/digits so accented additions (e.g. "3e étage")
  // survive; only strip punctuation we never want in a label.
  const onAdditionChange = (raw: string) =>
    setAddition(raw.replace(/[^\p{L}\p{N}\s\-/]/gu, "").slice(0, 20));

  // NL autofill: re-run whenever postcode/houseNumber/country changes and
  // the postcode looks valid. Aborting the previous request prevents a
  // slow earlier response from overwriting newer user input.
  useEffect(() => {
    if (country !== "NL") {
      setLookupState("idle");
      return;
    }
    if (!postalCode || !houseNumber) {
      setLookupState("idle");
      return;
    }
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLookupState("loading");
    const t = setTimeout(async () => {
      try {
        const res = await lookupNlAddress(postalCode, houseNumber, ctrl.signal);
        if (ctrl.signal.aborted) return;
        if (res) {
          setStreet(res.street);
          setCity(res.city);
          setLookupState("idle");
        } else {
          setLookupState("notFound");
        }
      } catch {
        if (!ctrl.signal.aborted) setLookupState("idle");
      }
    }, 250);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
    // setStreet/setCity are stable setState fns — including them would
    // re-run the effect needlessly on parent re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, postalCode, houseNumber]);

  const example = postcodeExample(country);
  const showLookupNote =
    country === "NL" && lookupState === "notFound" && hints?.lookupNotFound;

  return (
    <>
      <div className="grid grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,0.85fr)] gap-3">
        <FormField
          label={labels.postalCode}
          name="postalCode"
          value={postalCode}
          onChange={onPostcodeChange}
          onBlur={() => onBlur("postalCode")}
          required
          autoComplete="postal-code"
          inputMode="text"
          placeholder={example}
          error={errors.postalCode}
        />
        <FormField
          label={labels.houseNumber}
          name="houseNumber"
          value={houseNumber}
          onChange={onHouseNumberChange}
          onBlur={() => onBlur("houseNumber")}
          required
          autoComplete="off"
          inputMode={splitsHouseNumber ? "numeric" : "text"}
          placeholder={splitsHouseNumber ? "15" : undefined}
          error={errors.houseNumber}
        />
        <FormField
          label={labels.addition}
          name="addition"
          value={addition}
          onChange={onAdditionChange}
          autoComplete="off"
          inputMode="text"
          placeholder={labels.additionPlaceholder}
        />
      </div>
      <FormField
        label={labels.street}
        name="street"
        value={street}
        onChange={setStreet}
        onBlur={() => onBlur("street")}
        required
        autoComplete="address-line1"
        error={errors.street}
        hint={
          country === "NL" && lookupState === "loading"
            ? hints?.lookup
            : undefined
        }
      />
      <FormField
        label={labels.city}
        name="city"
        value={city}
        onChange={setCity}
        onBlur={() => onBlur("city")}
        required
        autoComplete="address-level2"
        error={errors.city}
      />
      {showLookupNote && (
        <p className="text-[12px] text-text-tertiary -mt-2">{hints?.lookupNotFound}</p>
      )}
    </>
  );
}
